import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import type { ContactFormValues } from "@/lib/contact-form";
import { findPhoneCountry } from "@/lib/phone-countries";

/** CMS contact submission body (POST /api/v1/contact-submissions). */
export type ContactSubmissionPayload = {
  name: string;
  email: string;
  phone_country_code: string;
  phone: string;
  company_name: string;
  expected_investment: string;
  message: string;
};

export type ContactSubmissionResult = {
  success: boolean;
  message?: string;
  data?: {
    id?: number;
    created_at?: string;
    errors?: Record<string, string[]>;
  };
};

const DEFAULT_CMS_ORIGIN = "https://cms.xattir.com";

/**
 * Browser: `NEXT_PUBLIC_CMS_URL` (no secrets). CORS on cms.xattir.com allows `*`,
 * so the mutation POSTs directly — no `/api/contact` proxy unless CORS regresses.
 */
export function getCmsOrigin(): string {
  const raw =
    process.env.NEXT_PUBLIC_CMS_URL?.trim() ||
    process.env.CMS_API_URL?.trim() ||
    DEFAULT_CMS_ORIGIN;
  return raw.replace(/\/$/, "");
}

export function contactSubmissionsUrl(): string {
  return `${getCmsOrigin()}/api/v1/contact-submissions`;
}

/** Dial like `+20` — API rejects ISO (`EG`) and bare digits (`20`). */
export function dialForIso(iso: string, locale = "en"): string {
  const known = findPhoneCountry(iso, locale)?.dial;
  if (known) return known;
  try {
    return `+${getCountryCallingCode(iso as CountryCode)}`;
  } catch {
    return "+20";
  }
}

/**
 * API wants national digits when `phone_country_code` is the dial.
 * Form Zod transform stores E.164 — strip to nationalNumber when possible.
 */
export function nationalPhoneForApi(phone: string, iso: string): string {
  const trimmed = phone.trim();
  if (!trimmed) return "";
  try {
    const fromE164 = parsePhoneNumberFromString(trimmed);
    if (fromE164?.isValid()) return fromE164.nationalNumber;
  } catch {
    /* fall through */
  }
  try {
    const fromNational = parsePhoneNumberFromString(trimmed, iso as never);
    if (fromNational?.isValid()) return fromNational.nationalNumber;
  } catch {
    /* fall through */
  }
  return trimmed.replace(/^\+/, "").replace(/\D/g, "") || trimmed;
}

export function formatExpectedInvestment(
  budget: string,
  currency: string,
): string {
  const amount = budget.trim();
  const cur = currency.trim();
  if (amount && cur) return `${amount} ${cur}`;
  return amount || cur;
}

export function toContactSubmissionPayload(
  values: ContactFormValues,
  locale = "en",
): ContactSubmissionPayload {
  return {
    name: values.name,
    email: values.email,
    phone_country_code: dialForIso(values.phoneCountry, locale),
    phone: nationalPhoneForApi(values.phone, values.phoneCountry),
    company_name: values.business,
    expected_investment: formatExpectedInvestment(
      values.budget,
      values.currency,
    ),
    message: values.details,
  };
}

export class ContactSubmissionError extends Error {
  readonly status: number;
  readonly body: ContactSubmissionResult | null;

  constructor(
    message: string,
    status: number,
    body: ContactSubmissionResult | null,
  ) {
    super(message);
    this.name = "ContactSubmissionError";
    this.status = status;
    this.body = body;
  }
}

export async function submitContactForm(
  values: ContactFormValues,
  locale = "en",
): Promise<ContactSubmissionResult> {
  const payload = toContactSubmissionPayload(values, locale);
  const res = await fetch(contactSubmissionsUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  const body = (await res.json().catch(() => null)) as ContactSubmissionResult | null;

  if (body?.success === true) {
    return body;
  }

  const fieldErrors = body?.data?.errors;
  const firstField =
    fieldErrors &&
    Object.values(fieldErrors)
      .flat()
      .find((msg) => Boolean(msg));
  const message =
    firstField ||
    body?.message ||
    (res.ok ? "Submission failed" : `Request failed (${res.status})`);
  throw new ContactSubmissionError(message, res.status, body);
}
